/**
 * MODULE: useAssistantVoiceInput.ts
 * PURPOSE: Web Speech API dictation + voice-mode (listen and send) for assistant composer.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

type WebSpeechRecognitionCtor = new () => {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onstart: (() => void) | null;
  onresult: ((event: { results: ArrayLike<{ 0: { transcript: string } }> }) => void) | null;
  onerror: (() => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
};

export type AssistantVoiceCaptureMode = 'dictation' | 'voice-mode';

export type UseAssistantVoiceInputOptions = {
  onAppendTranscript: (text: string) => void;
  onVoiceModeSend: (text: string) => void;
  disabled?: boolean;
};

export function useAssistantVoiceInput({
  onAppendTranscript,
  onVoiceModeSend,
  disabled = false,
}: UseAssistantVoiceInputOptions) {
  const [isSupported, setIsSupported] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [captureMode, setCaptureMode] = useState<AssistantVoiceCaptureMode | null>(null);

  const recognitionRef = useRef<InstanceType<WebSpeechRecognitionCtor> | null>(null);
  const captureModeRef = useRef<AssistantVoiceCaptureMode | null>(null);
  const onAppendRef = useRef(onAppendTranscript);
  const onVoiceSendRef = useRef(onVoiceModeSend);

  onAppendRef.current = onAppendTranscript;
  onVoiceSendRef.current = onVoiceModeSend;

  useEffect(() => {
    const win = window as typeof window & {
      SpeechRecognition?: WebSpeechRecognitionCtor;
      webkitSpeechRecognition?: WebSpeechRecognitionCtor;
    };
    const SpeechRecognitionCtor = win.SpeechRecognition || win.webkitSpeechRecognition;
    setIsSupported(Boolean(SpeechRecognitionCtor));

    if (!SpeechRecognitionCtor) return undefined;

    const recognition = new SpeechRecognitionCtor();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'en-US';

    recognition.onstart = () => setIsListening(true);
    recognition.onresult = (event: { results: ArrayLike<{ 0: { transcript: string } }> }) => {
      const transcript = Array.from(event.results)
        .map((result) => result[0]?.transcript ?? '')
        .join('')
        .trim();
      if (!transcript) return;

      const mode = captureModeRef.current;
      if (mode === 'voice-mode') {
        onVoiceSendRef.current(transcript);
      } else if (mode === 'dictation') {
        onAppendRef.current(transcript);
      }
    };
    recognition.onerror = () => {
      setIsListening(false);
      setCaptureMode(null);
      captureModeRef.current = null;
    };
    recognition.onend = () => {
      setIsListening(false);
      setCaptureMode(null);
      captureModeRef.current = null;
    };

    recognitionRef.current = recognition;
    return () => {
      recognition.stop();
      recognitionRef.current = null;
    };
  }, []);

  const stopListening = useCallback(() => {
    recognitionRef.current?.stop();
    setIsListening(false);
    setCaptureMode(null);
    captureModeRef.current = null;
  }, []);

  const startCapture = useCallback(
    (mode: AssistantVoiceCaptureMode) => {
      if (disabled || !recognitionRef.current) return;
      if (isListening && captureModeRef.current === mode) {
        stopListening();
        return;
      }
      if (isListening) stopListening();

      captureModeRef.current = mode;
      setCaptureMode(mode);
      try {
        recognitionRef.current.start();
      } catch {
        setIsListening(false);
        setCaptureMode(null);
        captureModeRef.current = null;
      }
    },
    [disabled, isListening, stopListening],
  );

  const toggleDictation = useCallback(() => {
    startCapture('dictation');
  }, [startCapture]);

  const toggleVoiceMode = useCallback(() => {
    startCapture('voice-mode');
  }, [startCapture]);

  return {
    isSupported,
    isListening,
    isDictating: isListening && captureMode === 'dictation',
    isVoiceMode: isListening && captureMode === 'voice-mode',
    toggleDictation,
    toggleVoiceMode,
    stopListening,
  };
}
